/**
 * Utility functions for scope-based translations in Menu Maker module
 * Provides translation keys based on the selected menu scope (STORE/ORGANIZATION)
 *
 * @module utils/menuMaker/scopeTranslations
 */

/**
 * Interface for scope-based translation keys
 *
 * @interface ScopeTranslationKeys
 */
export interface ScopeTranslationKeys {
  /** Translation key for title */
  titleKey: string;
  /** Translation key for description */
  descriptionKey: string;
}

/**
 * Gets scope-based translation keys for menu maker sections
 * Returns appropriate translation keys based on the selected scope
 *
 * @param {boolean} isOrganizationMode - Whether currently in organization mode
 * @param {string} section - The menu maker section (resume, menus, titleCategories, articles, customizableGroups, publish)
 * @returns {ScopeTranslationKeys} Object with title and description translation keys
 *
 * @example
 * const { titleKey, descriptionKey } = getScopeBasedTranslationKeys(false, 'resume');
 * // Returns: { titleKey: 'menuMaker:headerForm.store.resume.title', descriptionKey: 'menuMaker:headerForm.store.resume.description' }
 */
export const getScopeBasedTranslationKeys = (isOrganizationMode: boolean, section: string): ScopeTranslationKeys => {
  const currentScope = isOrganizationMode ? 'organization' : 'store';

  const titleKey = `menuMaker:headerForm.${currentScope}.${section}.title`;
  const descriptionKey = `menuMaker:headerForm.${currentScope}.${section}.description`;

  return {
    titleKey,
    descriptionKey,
  };
};

/**
 * Gets scope-based translation key for a specific field
 * Utility function for getting individual translation keys
 *
 * @param {boolean} isOrganizationMode - Whether currently in organization mode
 * @param {string} section - The menu maker section
 * @param {'title' | 'description'} field - The specific field to get
 * @returns {string} Translation key
 *
 * @example
 * const titleKey = getScopeBasedTranslationKey(true, 'menus', 'title');
 * // Returns: 'menuMaker:headerForm.organization.menus.title'
 */
export const getScopeBasedTranslationKey = (
  isOrganizationMode: boolean,
  section: string,
  field: 'title' | 'description',
): string => {
  const { titleKey, descriptionKey } = getScopeBasedTranslationKeys(isOrganizationMode, section);
  return field === 'title' ? titleKey : descriptionKey;
};
